import { resolveTicketStatusCode, normalizeTicketStatusToken } from './portalCustomerStatus';

const PORTAL_NON_GRACE_REVIEW_STATUSES = new Set([
  'reopen',
  're_opened',
  'reopened',
  're_open',
  'in_progress',
  'inprogress',
  'open',
  'pending_customer',
  'pending_supervisor_approval',
  'closed',
  'cancelled',
  'canceled',
  'escalated',
]);

/** Epoch ms from backend ISO timestamp — use for business logic only. */
export function getGraceEndMs(graceEndsAt) {
  if (graceEndsAt == null || graceEndsAt === '') {
    return null;
  }
  const ms = new Date(graceEndsAt).getTime();
  return Number.isNaN(ms) ? null : ms;
}

/** Remaining ms until graceEndsAt; display-only — never use for eligibility gates. */
export function getGraceRemainingMs(graceEndsAt, nowMs = Date.now()) {
  const endMs = getGraceEndMs(graceEndsAt);
  if (endMs == null) {
    return null;
  }
  return Math.max(0, endMs - nowMs);
}

/** Human-readable remaining label; display-only. */
export function formatGraceRemainingDisplay(remainingMs) {
  if (remainingMs == null) {
    return '';
  }
  if (remainingMs <= 0) {
    return '0 seconds';
  }
  const totalSeconds = Math.ceil(remainingMs / 1000);
  if (totalSeconds < 60) {
    return totalSeconds === 1 ? '1 second' : `${totalSeconds} seconds`;
  }
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (seconds === 0) {
    return minutes === 1 ? '1 minute' : `${minutes} minutes`;
  }
  return `${minutes} minute${minutes === 1 ? '' : 's'} ${seconds} second${seconds === 1 ? '' : 's'}`;
}

export function isSupervisorApprovedStatus(ticket, statuses = []) {
  const code = resolveTicketStatusCode(ticket, statuses);
  if (code && PORTAL_NON_GRACE_REVIEW_STATUSES.has(code)) {
    return false;
  }
  if (code === 'supervisor_approved') {
    return true;
  }
  const name = String(ticket?.status || '').toLowerCase();
  return name.includes('supervisor approved');
}

export function isGracePeriodActive(ticket, statuses = [], nowMs = Date.now()) {
  if (!ticket) {
    return false;
  }
  if (ticket.closedAt != null && ticket.closedAt !== '') {
    return false;
  }
  if (!isSupervisorApprovedStatus(ticket, statuses)) {
    return false;
  }
  if (!ticket.graceEndsAt) {
    return false;
  }
  const graceEndMs = getGraceEndMs(ticket.graceEndsAt);
  return graceEndMs != null && graceEndMs > nowMs;
}

function isPortalTicketClosed(ticket, statuses = []) {
  if (!ticket) {
    return true;
  }
  if (ticket.closedAt != null && ticket.closedAt !== '') {
    return true;
  }
  return resolveTicketStatusCode(ticket, statuses) === 'closed';
}

/** Resolution review / optional feedback — supervisor_approved and not closed; not grace-gated. */
export function showPortalResolutionReview(ticket, statuses = []) {
  if (isPortalTicketClosed(ticket, statuses)) {
    return false;
  }
  return isSupervisorApprovedStatus(ticket, statuses);
}

export function canGivePortalFeedback(ticket, statuses = []) {
  if (!ticket) {
    return false;
  }
  if (isPortalTicketClosed(ticket, statuses)) {
    return true;
  }
  return isSupervisorApprovedStatus(ticket, statuses);
}

export { normalizeTicketStatusToken };
